/*
 * /join/confirm?t=… — the brand signup's confirmation landing (design §8
 * Channel 1; row _design/DESIGN-BRAND-SIGNUP-AND-NEWSLETTERS-V1). Behind
 * VITE_BRAND_SIGNUP_ENABLED; mounted inside the authed shell.
 *
 * SIGN-IN FIRST: a signed-out visitor never reaches this component. The app's
 * auth gate renders the existing native email-code Login AT this path, and
 * Login's returnTo brings them back here (App.tsx also stashes the token —
 * storePendingJoinConfirm — for the fresh-tab sign-in link). First sign-in
 * creates or links the account by email, so there's never a duplicate person.
 *
 * Then POST /join/confirm {token} exactly once:
 *   200                    → My newsletters, that brand highlighted ON, plus
 *                            the optional "Help us tailor events" card
 *   409 already_confirmed  → treated as success (no highlight id on the wire)
 *   403 email_mismatch     → "sign in with the address you signed up with"
 *   410 link_expired       → "sign up again"
 * The token is never rendered or logged.
 */
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { clearSession, loadSession } from '../auth/session'
import { Card } from '../components/ui'
import { confirmJoin, myApiBase, MyApiError, storePendingJoinConfirm, takePendingJoinConfirm } from '../data/myApi'
import { useLocale } from '../i18n/useLocale'
import { NEWSLETTERS_PATH, type JoinedState } from '../components/MyNewsletters'

type Phase = 'working' | 'missing' | 'mismatch' | 'expired' | 'error'

export function JoinConfirm({ fetchImpl }: { fetchImpl?: typeof fetch }) {
  const { t } = useLocale()
  const navigate = useNavigate()
  const { search } = useLocation()
  const [token] = useState(() => {
    // The authed app is here now: drop the stash (one-shot).
    takePendingJoinConfirm()
    return (new URLSearchParams(search).get('t') ?? '').trim()
  })
  const [phase, setPhase] = useState<Phase>(token ? 'working' : 'missing')
  const [attempt, setAttempt] = useState(0)
  const fired = useRef(-1)

  useEffect(() => {
    if (!token || fired.current === attempt) return
    fired.current = attempt
    const session = loadSession()
    const base = myApiBase()
    if (!session || !base) {
      setPhase('error')
      return
    }
    setPhase('working')
    confirmJoin(session.idToken, base, token, fetchImpl ?? fetch)
      .then((r) => {
        const joined: JoinedState = { orgUnitId: r.org_unit_id, displayName: r.display_name, optedIn: r.opted_in }
        navigate(`${NEWSLETTERS_PATH}?joined=${encodeURIComponent(r.org_unit_id)}`, { replace: true, state: { joined } })
      })
      .catch((e) => {
        if (e instanceof MyApiError && (e.status === 409 || e.code === 'already_confirmed')) {
          const joined: JoinedState = { orgUnitId: null, displayName: null, optedIn: false }
          navigate(NEWSLETTERS_PATH, { replace: true, state: { joined } })
          return
        }
        if (e instanceof MyApiError && (e.status === 403 || e.code === 'email_mismatch')) setPhase('mismatch')
        else if (e instanceof MyApiError && (e.status === 410 || e.code === 'link_expired')) setPhase('expired')
        else setPhase('error')
      })
  }, [token, attempt, navigate, fetchImpl])

  const switchAccount = () => {
    // Keep the link for the next sign-in, then sign out: the auth gate shows
    // Login at this same path and returns here.
    storePendingJoinConfirm(token)
    clearSession()
    window.location.reload()
  }

  return (
    <div data-testid="join-confirm" style={{ maxWidth: 440, margin: '24px auto' }}>
      <Card>
        <div className="col" style={{ padding: 20, gap: 12, textAlign: 'center' }}>
          <h1 style={{ fontSize: 18, margin: 0 }}>{t('joinConfirm.title')}</h1>
          {phase === 'working' && <p role="status" className="secondary" style={{ fontSize: 13, margin: 0 }}>{t('joinConfirm.working')}</p>}
          {phase === 'missing' && <p data-testid="join-confirm-missing" role="alert" className="secondary" style={{ fontSize: 13, margin: 0 }}>{t('joinConfirm.missing')}</p>}
          {phase === 'expired' && <p data-testid="join-confirm-expired" role="alert" className="secondary" style={{ fontSize: 13, margin: 0 }}>{t('joinConfirm.expired')}</p>}
          {phase === 'mismatch' && (
            <>
              <p data-testid="join-confirm-mismatch" role="alert" className="secondary" style={{ fontSize: 13, margin: 0 }}>{t('joinConfirm.mismatch')}</p>
              <button type="button" data-testid="join-confirm-switch" onClick={switchAccount} style={outlineBtn}>{t('joinConfirm.switchAccount')}</button>
            </>
          )}
          {phase === 'error' && (
            <>
              <p data-testid="join-confirm-error" role="alert" className="secondary" style={{ fontSize: 13, margin: 0 }}>{t('joinConfirm.error')}</p>
              <button type="button" data-testid="join-confirm-retry" onClick={() => setAttempt((a) => a + 1)} style={outlineBtn}>{t('joinConfirm.retry')}</button>
            </>
          )}
        </div>
      </Card>
    </div>
  )
}

const outlineBtn = {
  minHeight: 44, borderRadius: 'var(--r-btn)', border: '1px solid var(--hairline-strong)', background: 'transparent',
  color: 'var(--text)', fontFamily: 'inherit', fontWeight: 800, fontSize: 13, cursor: 'pointer', padding: '0 18px',
} as const
